import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Bug, CheckCircle2 } from "lucide-react";
import { getBugReportData } from "@/utils/logCollector";

interface BugReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MIN_DESCRIPTION_LENGTH = 10;
const MAX_DESCRIPTION_LENGTH = 2000;
const MAX_DEVICE_LENGTH = 200;

const BugReportDialog: React.FC<BugReportDialogProps> = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const { user, profile } = useAuth();

  const [description, setDescription] = useState("");
  const [deviceInput, setDeviceInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const characterCount = description.length;
  const isValidLength = characterCount >= MIN_DESCRIPTION_LENGTH;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidLength) {
      toast.error(
        t(
          `Popis musí mít alespoň ${MIN_DESCRIPTION_LENGTH} znaků`,
          `Popis musí mať aspoň ${MIN_DESCRIPTION_LENGTH} znakov`
        )
      );
      return;
    }

    if (!user) {
      toast.error(t("Musíte být přihlášeni", "Musíte byť prihlásení"));
      return;
    }

    setIsSubmitting(true);

    try {
      const bugReportData = await getBugReportData();
      const trimmedDevice = deviceInput.trim();

      const { error } = await supabase.functions.invoke("submit-bug-report", {
        body: {
          description: description.trim(),
          pageUrl: bugReportData.browserInfo.currentUrl,
          userAgent: bugReportData.browserInfo.userAgent,
          consoleLogs: bugReportData.logs,
          userProvidedDevice: trimmedDevice || null,
          metadata: {
            browserInfo: bugReportData.browserInfo,
            userRole: profile?.role,
            userProvidedDevice: trimmedDevice || null,
            timestamp: bugReportData.timestamp,
          },
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to submit bug report");
      }

      setIsSubmitted(true);
      toast.success(t("Děkujeme! Váš report byl odeslán.", "Ďakujeme! Váš report bol odoslaný."));

      // Reset and close after showing success
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      console.error("Failed to submit bug report:", error);
      toast.error(
        t(
          "Nepodařilo se odeslat report. Zkuste to prosím znovu.",
          "Nepodarilo sa odoslať report. Skúste to prosím znova."
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setDescription("");
    setDeviceInput("");
    setIsSubmitted(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-primary" />
            {t("Nahlásit chybu", "Nahlásiť chybu")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Popište stručně, co nefunguje správně. Automaticky shromažďujeme technické informace pro diagnostiku.",
              "Popíšte stručne, čo nefunguje správne. Automaticky zbierame technické informácie pre diagnostiku."
            )}
          </DialogDescription>
        </DialogHeader>

        {isSubmitted ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
            <p className="text-lg font-medium">
              {t("Děkujeme za zpětnou vazbu!", "Ďakujeme za spätnú väzbu!")}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {t("Váš report byl úspěšně odeslán.", "Váš report bol úspešne odoslaný.")}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bugDescription">{t("Popis problému", "Popis problému")}</Label>
              <Textarea
                id="bugDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
                placeholder={t(
                  "Popište stručně, co se stalo a co jste očekávali...",
                  "Popíšte stručne, čo sa stalo a čo ste očakávali..."
                )}
                className="min-h-[120px] resize-none"
                disabled={isSubmitting}
                autoFocus
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {!isValidLength && characterCount > 0 && (
                    <span className="text-destructive">
                      {t(
                        `Minimum ${MIN_DESCRIPTION_LENGTH} znaků`,
                        `Minimálne ${MIN_DESCRIPTION_LENGTH} znakov`
                      )}
                    </span>
                  )}
                </span>
                <span
                  className={characterCount > MAX_DESCRIPTION_LENGTH * 0.9 ? "text-amber-500" : ""}
                >
                  {characterCount}/{MAX_DESCRIPTION_LENGTH}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bugDevice" className="flex items-center gap-2">
                <span>{t("Zařízení", "Zariadenie")}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  ({t("nepovinné", "nepovinné")})
                </span>
              </Label>
              <Input
                id="bugDevice"
                value={deviceInput}
                onChange={(e) => setDeviceInput(e.target.value.slice(0, MAX_DEVICE_LENGTH))}
                placeholder={t(
                  "např. iPhone 14 Pro, iPad Air, Samsung Galaxy S23, MacBook Pro 14",
                  "napr. iPhone 14 Pro, iPad Air, Samsung Galaxy S23, MacBook Pro 14"
                )}
                disabled={isSubmitting}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "Můžete upřesnit zařízení, na kterém se chyba objevila. Pomůže nám to s diagnostikou.",
                  "Môžete upresniť zariadenie, na ktorom sa chyba objavila. Pomôže nám to s diagnostikou."
                )}
              </p>
            </div>

            <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
              <p className="font-medium mb-1">
                {t("Automaticky shromažďujeme:", "Automaticky zbierame:")}
              </p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>{t("Aktuální URL stránky", "Aktuálna URL stránky")}</li>
                <li>
                  {t(
                    "Operační systém, prohlížeč a typ zařízení",
                    "Operačný systém, prehliadač a typ zariadenia"
                  )}
                </li>
                <li>{t("Nedávné chybové hlášky", "Nedávne chybové hlášky")}</li>
              </ul>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                {t("Zrušit", "Zrušiť")}
              </Button>
              <Button type="submit" disabled={isSubmitting || !isValidLength}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("Odeslat report", "Odoslať report")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BugReportDialog;
